import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type {
  StageNotificationConfig,
  StageNotificationType,
  CandidateStage,
  OpenRoleDetail,
} from '@onboarding-dashboard/shared';
import {
  STAGE_NOTIFICATION_TYPE_LABELS,
  STAGE_NOTIFICATION_TYPE_DESCRIPTIONS,
  STAGE_NOTIFICATION_TYPES_BY_STAGE,
  CANDIDATE_STAGE_LABELS,
} from '@onboarding-dashboard/shared';
import { adminService } from '../../services/admin.js';
import { rolesService } from '../../services/roles.js';
import './notification-settings.css';

const STAGE_OPTIONS = Object.entries(CANDIDATE_STAGE_LABELS).filter(
  ([key]) => key !== 'withdrawn' && key in STAGE_NOTIFICATION_TYPES_BY_STAGE
) as [string, string][];

interface FeedbackMessage {
  type: 'error' | 'success';
  text: string;
}

export function NotificationSettingsPage(): React.ReactElement {
  const [roles, setRoles] = useState<OpenRoleDetail[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [configs, setConfigs] = useState<StageNotificationConfig[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackMessage | null>(null);

  // ─── Add-form state ────────────────────────────────────────
  const [stage, setStage] = useState('');
  const [notificationType, setNotificationType] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // ─── Copy-form state ──────────────────────────────────────
  const [copySourceId, setCopySourceId] = useState('');
  const [isCopying, setIsCopying] = useState(false);

  const availableTypes = stage
    ? (STAGE_NOTIFICATION_TYPES_BY_STAGE[stage as CandidateStage] ?? [])
    : [];

  const canAdd = !!selectedRoleId && !!stage && !!notificationType && !!recipientEmail.trim();

  // ─── Feedback helpers ──────────────────────────────────────
  const showFeedback = (type: 'error' | 'success', text: string) => {
    setFeedback({ type, text });
    if (type === 'success') {
      setTimeout(() => setFeedback((prev) => (prev?.text === text ? null : prev)), 4000);
    }
  };

  const clearError = () => {
    setFeedback((prev) => (prev?.type === 'error' ? null : prev));
  };

  // ─── Load roles ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await rolesService.list({ status: 'open', pageSize: 100 });
        if (cancelled) return;
        if (!res.success || !res.data)
          throw new Error(res.error?.message ?? 'Failed to load roles');
        setRoles(res.data.items);
      } catch (err) {
        if (!cancelled)
          showFeedback('error', err instanceof Error ? err.message : 'Failed to load roles');
      } finally {
        if (!cancelled) setIsLoadingRoles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Load configs when selected role changes ───────────────
  const loadConfigs = useCallback(async (roleId: string) => {
    if (!roleId) {
      setConfigs([]);
      return;
    }
    setIsLoadingConfigs(true);
    try {
      const res = await adminService.listStageNotificationConfigs([roleId]);
      if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to load');
      setConfigs(res.data);
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to load configs');
    } finally {
      setIsLoadingConfigs(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs(selectedRoleId);
  }, [selectedRoleId, loadConfigs]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleAdd = async () => {
    if (!canAdd) return;
    setIsAdding(true);
    clearError();
    try {
      const res = await adminService.createStageNotificationConfig({
        openRoleId: selectedRoleId,
        stage: stage as CandidateStage,
        recipientEmail: recipientEmail.trim(),
        notificationType: notificationType as StageNotificationType,
      });
      if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to add');
      setConfigs((prev) => [...prev, res.data!]);
      setStage('');
      setNotificationType('');
      setRecipientEmail('');
      showFeedback('success', 'Notification config added.');
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to add config');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    clearError();
    setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, enabled } : c)));
    try {
      const res = await adminService.updateStageNotificationConfig(id, enabled);
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to update');
    } catch (err) {
      setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, enabled: !enabled } : c)));
      showFeedback('error', err instanceof Error ? err.message : 'Failed to update config');
    }
  };

  const handleDelete = async (id: string) => {
    clearError();
    try {
      const res = await adminService.deleteStageNotificationConfig(id);
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to delete');
      setConfigs((prev) => prev.filter((c) => c.id !== id));
      showFeedback('success', 'Notification config deleted.');
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to delete config');
    }
  };

  const handleCopy = async () => {
    if (!copySourceId || !selectedRoleId) return;
    setIsCopying(true);
    clearError();
    try {
      const res = await adminService.copyStageNotificationConfigs(copySourceId, selectedRoleId);
      if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to copy');
      // Refresh configs for the current role
      loadConfigs(selectedRoleId);
      showFeedback('success', `Copied ${res.data.copied} config(s).`);
      setCopySourceId('');
    } catch (err) {
      showFeedback('error', err instanceof Error ? err.message : 'Failed to copy configs');
    } finally {
      setIsCopying(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────
  const typeDescription = notificationType
    ? (STAGE_NOTIFICATION_TYPE_DESCRIPTIONS[notificationType as StageNotificationType] ?? '')
    : '';

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  return (
    <div className="notif-settings-page">
      <div className="notif-settings-container">
        <Link to="/pipeline" className="back-link">
          &larr; Pipeline
        </Link>
        <div className="notif-settings-header">
          <h1>Stage Notification Settings</h1>
          <p>
            Configure who receives email notifications when candidates reach each pipeline stage.
          </p>
        </div>

        {feedback?.type === 'error' && (
          <div className="notif-settings-error" role="alert">
            <span>{feedback.text}</span>
          </div>
        )}
        {feedback?.type === 'success' && (
          <div className="notif-settings-success" role="status">
            <span>{feedback.text}</span>
          </div>
        )}

        <div className="notif-settings-role-selector">
          <label htmlFor="notif-role">Role</label>
          <select
            id="notif-role"
            className="form-select"
            value={selectedRoleId}
            disabled={isLoadingRoles}
            onChange={(e) => setSelectedRoleId(e.target.value)}
          >
            <option value="">Select a role…</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.title} — {role.department}
              </option>
            ))}
          </select>
        </div>

        {selectedRoleId && (
          <>
            <div className="notif-settings-add-card">
              <h2 className="notif-settings-add-title">
                Add Notification for {selectedRole?.title ?? 'Role'}
              </h2>
              <div className="notif-settings-form">
                <div className="notif-settings-form-row">
                  <div className="notif-settings-field">
                    <label htmlFor="notif-stage">Stage</label>
                    <select
                      id="notif-stage"
                      className="form-select"
                      value={stage}
                      onChange={(e) => {
                        const newStage = e.target.value;
                        setStage(newStage);
                        const types = newStage
                          ? (STAGE_NOTIFICATION_TYPES_BY_STAGE[newStage as CandidateStage] ?? [])
                          : [];
                        setNotificationType(types.length === 1 ? types[0]! : '');
                      }}
                    >
                      <option value="">Select stage…</option>
                      {STAGE_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {availableTypes.length > 1 && (
                    <div className="notif-settings-field">
                      <label htmlFor="notif-type">Notification Type</label>
                      <select
                        id="notif-type"
                        className="form-select"
                        value={notificationType}
                        onChange={(e) => setNotificationType(e.target.value)}
                      >
                        <option value="">Select type…</option>
                        {availableTypes.map((value) => (
                          <option key={value} value={value}>
                            {STAGE_NOTIFICATION_TYPE_LABELS[value]}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="notif-settings-field notif-settings-field--email">
                    <label htmlFor="notif-email">Recipient Email</label>
                    <input
                      type="email"
                      id="notif-email"
                      className="form-input"
                      placeholder="email@example.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                    />
                  </div>
                  <button
                    className="btn-add-notif"
                    disabled={!canAdd || isAdding}
                    onClick={handleAdd}
                  >
                    Add
                  </button>
                </div>
                {typeDescription && <p className="notif-settings-type-desc">{typeDescription}</p>}
              </div>

              <div className="notif-settings-copy-divider"></div>

              <h2 className="notif-settings-add-title">Copy from Another Role</h2>
              <div className="notif-settings-form">
                <div className="notif-settings-form-row">
                  <div className="notif-settings-field">
                    <label htmlFor="copy-source">Source Role</label>
                    <select
                      id="copy-source"
                      className="form-select"
                      value={copySourceId}
                      onChange={(e) => setCopySourceId(e.target.value)}
                    >
                      <option value="">Copy from…</option>
                      {roles
                        .filter((r) => r.id !== selectedRoleId)
                        .map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.title}
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    className="btn-add-notif"
                    disabled={!copySourceId || isCopying}
                    onClick={handleCopy}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <section className="notif-settings-section">
              {isLoadingConfigs && (
                <div className="notif-settings-loading">
                  <p>Loading notification configs…</p>
                </div>
              )}

              {!isLoadingConfigs && configs.length === 0 && (
                <div className="notif-settings-empty">
                  <p>No notification configs for this role yet. Add one above.</p>
                </div>
              )}

              {!isLoadingConfigs && configs.length > 0 && (
                <div className="notif-settings-table-wrapper">
                  <table className="notif-settings-table">
                    <thead>
                      <tr>
                        <th>Stage</th>
                        <th>Notification Type</th>
                        <th>Recipient</th>
                        <th>Enabled</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configs.map((cfg) => (
                        <tr key={cfg.id}>
                          <td>
                            <span className="stage-badge">
                              {CANDIDATE_STAGE_LABELS[cfg.stage] ?? cfg.stage}
                            </span>
                          </td>
                          <td>
                            {STAGE_NOTIFICATION_TYPE_LABELS[
                              cfg.notificationType as StageNotificationType
                            ] ?? cfg.notificationType}
                          </td>
                          <td>{cfg.recipientEmail}</td>
                          <td>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={cfg.enabled}
                                onChange={(e) => handleToggle(cfg.id, e.target.checked)}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </td>
                          <td>
                            <button
                              className="btn-delete-notif"
                              onClick={() => handleDelete(cfg.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {!selectedRoleId && !isLoadingRoles && (
          <div className="notif-settings-empty">
            <p>Select a role above to manage its notification settings.</p>
          </div>
        )}
      </div>
    </div>
  );
}
